/**
 * SimulationPanel Component
 * Controls for simulating multi-turn conversations
 */

import React, { useState } from 'react';
import { FiZap, FiStopCircle } from 'react-icons/fi';

interface SimulationPanelProps {
  onSimulate: (userMessage: string, botResponse: string, turns: number) => void;
  onStop: () => void;
  isSimulating: boolean;
  disabled?: boolean;
  className?: string;
}

export const SimulationPanel: React.FC<SimulationPanelProps> = ({
  onSimulate,
  onStop,
  isSimulating,
  disabled = false,
  className = ''
}) => {
  const [userMessage, setUserMessage] = useState('I need help with my account');
  const [botResponse, setBotResponse] = useState('I can help you with that');
  const [turns, setTurns] = useState(5);

  const handleSimulate = () => {
    if (userMessage.trim() && botResponse.trim() && turns > 0) {
      onSimulate(userMessage.trim(), botResponse.trim(), turns);
    }
  };

  const canSimulate = !disabled && !isSimulating && userMessage.trim() && botResponse.trim() && turns > 0;

  return (
    <div className={`w-80 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <FiZap className="text-purple-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Conversation Simulation</h3>
        </div>
        <p className="text-sm text-gray-400 mt-1">
          Test multi-turn conversations automatically
        </p>
      </div>

      {/* Controls */}
      <div className="flex-1 p-4 space-y-4">
        {/* User Message Template */}
        <div>
          <label htmlFor="user-message" className="block text-sm font-medium text-gray-300 mb-2">
            User Message Pattern
          </label>
          <textarea
            id="user-message"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            disabled={disabled || isSimulating}
            rows={3}
            placeholder="Enter typical user message..."
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>

        {/* Bot Response Template */}
        <div>
          <label htmlFor="bot-response" className="block text-sm font-medium text-gray-300 mb-2">
            Expected Bot Response
          </label>
          <textarea
            id="bot-response"
            value={botResponse}
            onChange={(e) => setBotResponse(e.target.value)}
            disabled={disabled || isSimulating}
            rows={3}
            placeholder="Enter expected response pattern..."
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>

        {/* Number of Turns */}
        <div>
          <label htmlFor="turns" className="block text-sm font-medium text-gray-300 mb-2">
            Number of Turns: {turns}
          </label>
          <input
            id="turns"
            type="range"
            min="1"
            max="20"
            value={turns}
            onChange={(e) => setTurns(parseInt(e.target.value))}
            disabled={disabled || isSimulating}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>10</span>
            <span>20</span>
          </div>
        </div>

        {/* Info */}
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
          <p className="text-xs text-purple-300">
            The simulation will generate {turns} conversation turns based on your templates,
            helping you test assistant behavior at scale.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          {!isSimulating ? (
            <button
              onClick={handleSimulate}
              disabled={!canSimulate}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                canSimulate
                  ? 'bg-purple-500 hover:bg-purple-600 text-white'
                  : 'bg-slate-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <FiZap size={20} />
              <span>Start Simulation</span>
            </button>
          ) : (
            <button
              onClick={onStop}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
            >
              <FiStopCircle size={20} />
              <span>Stop Simulation</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulationPanel;
